import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Languages, LogOut, ScanLine, LayoutDashboard, Users2, BarChart3, MessageCircle,
  Moon, Sun, User, ScrollText, Megaphone, Trophy, Clock, Menu,
} from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { ChangePasswordDialog } from "./ChangePasswordDialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";

function useDarkMode() {
  const [dark, setDark] = React.useState(() => localStorage.getItem("theme") === "dark");
  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
}

function DrawerLink({ to, icon: Icon, label, badge, active, onNavigate, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      onClick={onNavigate}
      className={`relative flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
        active ? "bg-blue-600 text-white" : "text-slate-700 hover:bg-blue-50 hover:text-blue-700"
      }`}
    >
      <Icon className="w-[18px] h-[18px] shrink-0" />
      <span className="flex-1">{label}</span>
      {badge > 0 && (
        <span
          data-testid="drawer-support-unread-badge"
          className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold grid place-items-center"
        >
          {badge}
        </span>
      )}
    </Link>
  );
}

export default function Header({ showAdminNav = false }) {
  const { t, toggleLang, lang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [dark, setDark] = useDarkMode();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [supportUnread, setSupportUnread] = React.useState(0);

  const isAdmin = user && user !== false && user.role === "admin";
  const isSupervisor = user && user !== false && user.role === "supervisor";
  const isUser = user && user !== false && user.role === "user";
  const showDrawer = showAdminNav && (isAdmin || isSupervisor);

  React.useEffect(() => {
    if (showAdminNav && isAdmin) {
      api
        .get("/support/threads")
        .then((r) => setSupportUnread(r.data.reduce((a, th) => a + (th.unread || 0), 0)))
        .catch(() => {});
    }
  }, [showAdminNav, isAdmin]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logout();
    navigate("/");
  };

  const closeMenu = () => setMenuOpen(false);

  const adminLinks = [
    { to: "/admin", icon: LayoutDashboard, label: t("dashboard"), testid: "nav-dashboard" },
    { to: "/admin/scanner", icon: ScanLine, label: t("scanner"), testid: "nav-scanner" },
    { to: "/admin/stats", icon: BarChart3, label: t("statsPage"), testid: "nav-stats" },
    { to: "/admin/hours", icon: Clock, label: t("volunteerHours"), testid: "nav-hours" },
    { to: "/admin/supervisors", icon: Users2, label: t("supervisors"), testid: "nav-supervisors" },
    { to: "/admin/support", icon: MessageCircle, label: t("supportChat"), badge: supportUnread, testid: "nav-support" },
    { to: "/admin/leaderboard", icon: Trophy, label: t("manageLeaderboard"), testid: "nav-leaderboard" },
    ...(user?.is_super
      ? [
          { to: "/admin/audit", icon: ScrollText, label: t("auditLog"), testid: "nav-audit" },
          { to: "/admin/broadcast", icon: Megaphone, label: t("broadcastNav"), testid: "nav-broadcast" },
        ]
      : []),
  ];

  const supervisorLinks = [
    { to: "/admin/scanner", icon: ScanLine, label: t("scanner"), testid: "nav-scanner" },
    { to: "/admin/stats", icon: BarChart3, label: t("statsPage"), testid: "nav-stats-supervisor" },
  ];

  const links = isAdmin ? adminLinks : supervisorLinks;
  const displayName = user && user !== false ? user.name || user.email : "";

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {showDrawer && (
            <button
              data-testid="admin-menu-btn"
              onClick={() => setMenuOpen(true)}
              className="relative w-10 h-10 grid place-items-center rounded-xl text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors"
              aria-label="menu"
            >
              <Menu className="w-6 h-6" />
              {supportUnread > 0 && (
                <span className="absolute top-1.5 end-1.5 w-2.5 h-2.5 rounded-full bg-red-600 animate-pulse" />
              )}
            </button>
          )}
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-600 to-red-500 grid place-items-center text-white font-bold text-lg">
              V
            </div>
            <span className="font-display text-lg font-semibold text-slate-900">{t("brand")}</span>
          </Link>
        </div>

        {showDrawer ? (
          <div className="flex items-center gap-2" data-testid="header-user-chip">
            {isSupervisor && (
              <span className="hidden sm:inline px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-medium">
                {t("supervisorMode")}
              </span>
            )}
            <div className="text-end hidden sm:block">
              <p className="text-sm font-semibold text-slate-900 leading-tight">{displayName}</p>
              <p className="text-[11px] text-slate-500 leading-tight">
                {isAdmin ? t("adminPanel") : t("supervisorMode")}
              </p>
            </div>
            <Link
              to="/account"
              data-testid="header-avatar-link"
              className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 grid place-items-center font-bold text-sm uppercase hover:bg-blue-200 transition-colors"
            >
              {(displayName || "?").charAt(0)}
            </Link>
          </div>
        ) : (
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              data-testid="lang-toggle-btn"
              variant="ghost"
              size="sm"
              onClick={toggleLang}
              className="text-slate-700"
            >
              <Languages className="w-4 h-4 sm:me-1.5" />
              <span className="hidden sm:inline">{t("languageToggle")}</span>
            </Button>
            <Button
              data-testid="dark-toggle-btn"
              variant="ghost"
              size="sm"
              onClick={() => setDark(!dark)}
              className="text-slate-700"
              title={t("darkMode")}
            >
              {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            {user && user !== false ? (
              <>
                <Link to={isAdmin ? "/admin" : isSupervisor ? "/admin/scanner" : "/account"}>
                  <Button
                    data-testid="header-dashboard-btn"
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                  >
                    {isUser ? <User className="w-4 h-4 sm:me-1.5" /> : <LayoutDashboard className="w-4 h-4 sm:me-1.5" />}
                    <span className="hidden sm:inline">{isAdmin ? t("openDashboard") : isSupervisor ? t("scanner") : t("myAccount")}</span>
                  </Button>
                </Link>
                <ChangePasswordDialog />
                <Button
                  data-testid="logout-btn"
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 rounded-full"
                >
                  <LogOut className="w-4 h-4 sm:me-1.5" />
                  <span className="hidden sm:inline">{t("logout")}</span>
                </Button>
              </>
            ) : (
              <Link to="/login">
                <Button
                  data-testid="header-login-btn"
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"
                >
                  {t("adminCta")}
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>

      {showDrawer && (
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent
            side={lang === "ar" ? "right" : "left"}
            className="w-[300px] sm:w-[320px] p-0 flex flex-col bg-[#FDFBF7]"
            data-testid="admin-drawer"
            aria-describedby={undefined}
          >
            <SheetHeader className="px-5 pt-5 pb-4 border-b border-slate-200 text-start">
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-red-500 grid place-items-center text-white font-bold text-xl">
                  V
                </div>
                <div>
                  <SheetTitle className="font-display text-base font-semibold text-slate-900 text-start">
                    {t("brand")}
                  </SheetTitle>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {displayName} · {isAdmin ? t("adminPanel") : t("supervisorMode")}
                  </p>
                </div>
              </div>
            </SheetHeader>

            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              {links.map((l) => (
                <DrawerLink
                  key={l.to + l.testid}
                  {...l}
                  active={location.pathname === l.to}
                  onNavigate={closeMenu}
                />
              ))}
            </nav>

            <div className="border-t border-slate-200 px-3 py-4 space-y-1">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Button
                  data-testid="drawer-lang-toggle-btn"
                  variant="outline"
                  size="sm"
                  onClick={toggleLang}
                  className="rounded-xl justify-center"
                >
                  <Languages className="w-4 h-4 me-1.5" />
                  {t("languageToggle")}
                </Button>
                <Button
                  data-testid="drawer-dark-toggle-btn"
                  variant="outline"
                  size="sm"
                  onClick={() => setDark(!dark)}
                  className="rounded-xl justify-center"
                >
                  {dark ? <Sun className="w-4 h-4 me-1.5" /> : <Moon className="w-4 h-4 me-1.5" />}
                  {t("darkMode")}
                </Button>
              </div>
              <ChangePasswordDialog />
              <Button
                data-testid="drawer-logout-btn"
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="w-full justify-start text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl px-4"
              >
                <LogOut className="w-4 h-4 me-3" />
                {t("logout")}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </header>
  );
}
